import { route, RouteKeys } from "@/services/routing/routes";

/**
 * Role identifiers matching backend RoleEnum slugs
 * These slugs are stable and won't change even if role IDs are renumbered
 */
export const ROLES = {
  ADMIN: "admin", // Platform admin (hey circle employee)
  USER: "user", // Basic user
  COMPANY_ADMIN: "company_admin", // Company administrator
  COMPANY_MANAGER: "company_manager", // Company manager
  COMPANY_SCANNER: "company_scanner", // Company scanner (scan operations)
  DROPSHIPPER: "dropshipper", // Dropshipper role
} as const;

export type RoleSlug = (typeof ROLES)[keyof typeof ROLES];

export interface NavigationItem {
  href: string;
  label: string;
  roles: RoleSlug[];
  /** Lucide icon name as string (e.g., "ScanLine", "Send", "MapPin") */
  icon?: string;
  /** Optional sub-navigation items rendered as a collapsible group */
  children?: NavigationItem[];
}

export const navigationItems: NavigationItem[] = [
  // Customer/Company navigation — 4 top-level items (no ADMIN role)
  {
    href: route(RouteKeys.SCANNER_SEND),
    label: "nav.ship",
    icon: "Send",
    roles: [ROLES.COMPANY_ADMIN, ROLES.COMPANY_MANAGER, ROLES.COMPANY_SCANNER, ROLES.USER],
  },
  {
    href: route(RouteKeys.SCANNER),
    label: "nav.receive",
    icon: "ScanLine",
    roles: [ROLES.COMPANY_ADMIN, ROLES.COMPANY_MANAGER, ROLES.COMPANY_SCANNER, ROLES.USER],
  },
  {
    href: route(RouteKeys.ANALYTICS),
    label: "nav.analytics",
    icon: "TrendingUp",
    roles: [ROLES.COMPANY_ADMIN, ROLES.COMPANY_MANAGER],
  },
  {
    href: "#",
    label: "nav.settings",
    icon: "Settings",
    roles: [ROLES.COMPANY_ADMIN, ROLES.COMPANY_MANAGER, ROLES.COMPANY_SCANNER],
    children: [
      {
        href: route(RouteKeys.LOCATIONS),
        label: "nav.scanLocations",
        roles: [ROLES.COMPANY_ADMIN, ROLES.COMPANY_MANAGER],
      },
      {
        href: route(RouteKeys.EXTERNAL_DESTINATIONS),
        label: "nav.externalDestinations",
        roles: [ROLES.COMPANY_ADMIN, ROLES.COMPANY_MANAGER],
      },
      {
        href: route(RouteKeys.SCANNER_PACKAGES),
        label: "nav.inventoryAll",
        roles: [ROLES.COMPANY_ADMIN, ROLES.COMPANY_MANAGER, ROLES.COMPANY_SCANNER],
      },
      {
        href: route(RouteKeys.CONTRACTS),
        label: "nav.contracts",
        roles: [ROLES.COMPANY_ADMIN, ROLES.COMPANY_MANAGER],
      },
      {
        href: route(RouteKeys.USERS_MANAGEMENT),
        label: "nav.users",
        roles: [ROLES.COMPANY_ADMIN],
      },
      {
        href: route(RouteKeys.SHOPIFY),
        label: "nav.integrations",
        roles: [ROLES.COMPANY_ADMIN],
      },
      {
        href: route(RouteKeys.BILLING),
        label: "nav.billing",
        roles: [ROLES.COMPANY_ADMIN],
      },
    ],
  },
  // Admin navigation (hey circle employees only)
  {
    href: route(RouteKeys.ADMIN_DASHBOARD),
    label: "nav.adminDashboard",
    roles: [ROLES.ADMIN],
  },
  {
    href: route(RouteKeys.ADMIN_COMPANIES),
    label: "nav.companies",
    roles: [ROLES.ADMIN],
  },
  {
    href: route(RouteKeys.ADMIN_PACKAGES),
    label: "nav.packages",
    roles: [ROLES.ADMIN],
  },
  {
    href: route(RouteKeys.ADMIN_CONTRACTS),
    label: "nav.adminContracts",
    roles: [ROLES.ADMIN],
  },
  {
    href: route(RouteKeys.ADMIN_USERS),
    label: "nav.users",
    roles: [ROLES.ADMIN],
  },
];

/**
 * Get navigation items for a user's role.
 * Filters both top-level items and their children by role.
 * If a parent has children but none match the role, the parent is excluded.
 */
export function getNavigationItems(roleSlug?: string): NavigationItem[] {
  if (!roleSlug) return [];
  return navigationItems
    .filter((item) => item.roles.includes(roleSlug as RoleSlug))
    .map((item) => {
      if (!item.children) return item;
      const filteredChildren = item.children.filter((child) =>
        child.roles.includes(roleSlug as RoleSlug)
      );
      if (filteredChildren.length === 0) return null;
      return { ...item, children: filteredChildren };
    })
    .filter((item): item is NavigationItem => item !== null);
}

/**
 * Check if a role can access a specific path.
 * Searches both top-level items and their children.
 */
export function canAccess(roleSlug: string, href: string): boolean {
  for (const item of navigationItems) {
    if (item.href === href) {
      return item.roles.includes(roleSlug as RoleSlug);
    }
    if (item.children) {
      const child = item.children.find((c) => c.href === href);
      if (child) {
        return child.roles.includes(roleSlug as RoleSlug);
      }
    }
  }
  return false;
}

/**
 * Check if user has a specific role
 */
export function hasRole(roleSlug: string, role: RoleSlug): boolean {
  return roleSlug === role;
}

/**
 * Check if user has any of the specified roles
 */
export function hasAnyRole(roleSlug: string, roles: RoleSlug[]): boolean {
  return roles.includes(roleSlug as RoleSlug);
}

/**
 * Check if user is a company admin
 */
export function isCompanyAdmin(roleSlug?: string): boolean {
  return roleSlug === ROLES.COMPANY_ADMIN;
}

/**
 * Check if user is a platform admin
 */
export function isPlatformAdmin(roleSlug?: string): boolean {
  return roleSlug === ROLES.ADMIN;
}

/**
 * Check if user is any kind of admin
 */
export function isAdmin(roleSlug?: string): boolean {
  return roleSlug === ROLES.ADMIN || roleSlug === ROLES.COMPANY_ADMIN;
}
